/**
 * Filtros, ordenacao e facetas da lista de leads -- funcoes PURAS.
 *
 * Tres consumidores usam exatamente as mesmas regras:
 *  1. o gerador simulado (mock.ts), fazendo o papel do servidor;
 *  2. a pagina, que reaplica filtro e ordem em memoria sobre o que carregou
 *     (idempotente quando o servidor ja filtrou; e o que faz o backend de
 *     hoje, que so aceita `limit/offset`, continuar honesto);
 *  3. o CSV ("exportar o que estou vendo").
 *
 * Os filtros COMBINAM (E logico): situacao + oferta + canal do ultimo
 * contato + funil + alto valor + busca. A busca ignora acento e caixa e
 * olha nome, produto e digitos do telefone (3+ digitos) -- e assim que o
 * suporte procura alguem ("o final 0003").
 *
 * A consulta mora na URL (`?situacao=esgotado&alto=1&ordem=amount`): o link
 * de um insight de outra pagina abre a lista ja recortada, o botao "voltar"
 * do detalhe cai no mesmo recorte e a visao pode ser compartilhada. O
 * periodo (`periodo`, `de`, `ate`) e do cabecalho e nunca e tocado aqui.
 */

import type { FunnelKind, LeadListItem } from '../../types';
import {
  ORDEM_DAS_SITUACOES,
  type CanalDoFiltro,
  type ConsultaDeLeads,
  type DirecaoDaOrdem,
  type FacetasDeLeads,
  type LeadListRow,
  type OrdenacaoDeLeads,
  type SituacaoDoLead,
} from './types';

export const PAGE_SIZE = 25;

export const CONSULTA_PADRAO: ConsultaDeLeads = {
  limit: PAGE_SIZE,
  offset: 0,
  sort: 'abandonedAt',
  dir: 'desc',
  q: '',
  situacao: null,
  offerId: null,
  canal: null,
  funnel: null,
  altoValor: false,
};

/** Valor do chip "Tudo" (chips sao `string`; `null` na consulta). */
export const TUDO = 'tudo';

const CANAIS: readonly CanalDoFiltro[] = ['voice', 'whatsapp', 'email', 'seller', 'nenhum'];
const ORDENS: readonly OrdenacaoDeLeads[] = ['abandonedAt', 'amount', 'situacao'];
const FUNIS: readonly FunnelKind[] = ['carrinho_abandonado', 'expirados'];

export function normalizarTexto(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

export function combinaComBusca(lead: LeadListItem, busca: string): boolean {
  const termo = normalizarTexto(busca);
  if (termo === '') {
    return true;
  }
  if (lead.name !== null && normalizarTexto(lead.name).includes(termo)) {
    return true;
  }
  if (lead.productName !== null && normalizarTexto(lead.productName).includes(termo)) {
    return true;
  }
  const digitos = termo.replace(/\D/g, '');
  if (digitos.length >= 3 && lead.phoneE164.replace(/\D/g, '').includes(digitos)) {
    return true;
  }
  return false;
}

function canalDaLinha(row: LeadListRow): CanalDoFiltro {
  return row.lastContact === null ? 'nenhum' : row.lastContact.channel;
}

/** Aplica TODOS os filtros da consulta (chips, alto valor e busca). Nao pagina nem ordena. */
export function aplicarFiltros(rows: readonly LeadListRow[], consulta: ConsultaDeLeads): readonly LeadListRow[] {
  return rows.filter(
    (r) =>
      (consulta.situacao === null || r.situacao === consulta.situacao) &&
      (consulta.offerId === null || r.offerId === consulta.offerId) &&
      (consulta.canal === null || canalDaLinha(r) === consulta.canal) &&
      (consulta.funnel === null || r.funnel === consulta.funnel) &&
      (!consulta.altoValor || r.highValue) &&
      combinaComBusca(r, consulta.q),
  );
}

function indiceDaSituacao(s: SituacaoDoLead): number {
  const i = ORDEM_DAS_SITUACOES.indexOf(s);
  return i < 0 ? ORDEM_DAS_SITUACOES.length : i;
}

/** Ordenacao estavel: empate desempata pelo abandono mais recente. */
export function ordenar(rows: readonly LeadListRow[], sort: OrdenacaoDeLeads, dir: DirecaoDaOrdem): readonly LeadListRow[] {
  const sinal = dir === 'asc' ? 1 : -1;
  const copia = [...rows];
  copia.sort((a, b) => {
    let cmp = 0;
    switch (sort) {
      case 'amount':
        cmp = a.amountCents - b.amountCents;
        break;
      case 'situacao':
        cmp = indiceDaSituacao(a.situacao) - indiceDaSituacao(b.situacao);
        break;
      case 'abandonedAt':
        cmp = a.abandonedAt.localeCompare(b.abandonedAt);
        break;
      default: {
        const nunca: never = sort;
        return nunca;
      }
    }
    if (cmp !== 0) {
      return cmp * sinal;
    }
    return b.abandonedAt.localeCompare(a.abandonedAt);
  });
  return copia;
}

/** Contagens por dimensao sobre `rows`. `parciais` diz se `rows` e so a pagina carregada. */
export function contarFacetas(rows: readonly LeadListRow[], parciais: boolean): FacetasDeLeads {
  const porSituacao: Record<SituacaoDoLead, number> = { na_fila: 0, em_recuperacao: 0, com_comercial: 0, recuperado: 0, pagou_sozinho: 0, esgotado: 0, falhou: 0, cancelado: 0 };
  const porCanal: Record<CanalDoFiltro, number> = { voice: 0, whatsapp: 0, email: 0, seller: 0, nenhum: 0 };
  const porFunil: Record<FunnelKind, number> = { carrinho_abandonado: 0, expirados: 0 };
  const ofertas = new Map<string, { readonly offerId: string; readonly name: string; count: number }>();
  let altoValor = 0;
  for (const r of rows) {
    porSituacao[r.situacao] += 1;
    porCanal[canalDaLinha(r)] += 1;
    porFunil[r.funnel] += 1;
    if (r.highValue) {
      altoValor += 1;
    }
    if (r.offerId !== null) {
      const atual = ofertas.get(r.offerId);
      if (atual === undefined) {
        ofertas.set(r.offerId, { offerId: r.offerId, name: r.productName ?? r.offerId, count: 1 });
      } else {
        atual.count += 1;
      }
    }
  }
  const porOferta = [...ofertas.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'pt-BR'));
  return { porSituacao, porCanal, porFunil, porOferta, altoValor, parciais };
}

/** Quantos recortes alem do padrao estao ligados (para o botao "Limpar filtros (N)"). */
export function filtrosAtivos(consulta: ConsultaDeLeads): number {
  let n = 0;
  if (consulta.situacao !== null) {
    n += 1;
  }
  if (consulta.offerId !== null) {
    n += 1;
  }
  if (consulta.canal !== null) {
    n += 1;
  }
  if (consulta.funnel !== null) {
    n += 1;
  }
  if (consulta.altoValor) {
    n += 1;
  }
  if (consulta.q.trim() !== '') {
    n += 1;
  }
  return n;
}

/** Aproximacao da situacao quando o backend manda so o `LeadListItem` (sem linha do tempo). */
export function situacaoAproximada(item: LeadListItem): SituacaoDoLead {
  // Carrinho que chegou depois do pagamento: sem job, o `pending` do servidor
  // nao e fila -- o lead pagou sem toque nenhum e sem comissao.
  if (item.skipReason === 'ja_pago') {
    return 'pagou_sozinho';
  }
  switch (item.jobStatus) {
    case 'pending':
      return 'na_fila';
    case 'running':
      return 'em_recuperacao';
    case 'succeeded':
      return 'recuperado';
    case 'aborted':
      return item.recoveredAt !== null ? 'pagou_sozinho' : 'cancelado';
    case 'failed':
      return item.tier === 'exhausted' ? 'esgotado' : 'falhou';
    default: {
      const nunca: never = item.jobStatus;
      return nunca;
    }
  }
}

// -----------------------------------------------------------------------------
// URL
// -----------------------------------------------------------------------------

const CHAVES = { situacao: 'situacao', oferta: 'oferta', canal: 'canal', funil: 'funil', alto: 'alto', q: 'q', ordem: 'ordem', dir: 'dir' } as const;

function entre<T extends string>(valor: string | null, lista: readonly T[]): T | null {
  if (valor === null) {
    return null;
  }
  return lista.find((v) => v === valor) ?? null;
}

/** Le so o que a lista controla. O que nao esta na URL fica no padrao. */
export function lerConsultaDaUrl(search: string): ConsultaDeLeads {
  const params = new URLSearchParams(search);
  const ordem = entre(params.get(CHAVES.ordem), ORDENS) ?? CONSULTA_PADRAO.sort;
  const dir = entre(params.get(CHAVES.dir), ['asc', 'desc'] as const) ?? (ordem === 'abandonedAt' ? 'desc' : ordem === 'amount' ? 'desc' : 'asc');
  return {
    ...CONSULTA_PADRAO,
    sort: ordem,
    dir,
    q: params.get(CHAVES.q) ?? '',
    situacao: entre(params.get(CHAVES.situacao), ORDEM_DAS_SITUACOES),
    offerId: params.get(CHAVES.oferta),
    canal: entre(params.get(CHAVES.canal), CANAIS),
    funnel: entre(params.get(CHAVES.funil), FUNIS),
    altoValor: params.get(CHAVES.alto) === '1',
  };
}

/** Escreve a consulta preservando o resto da URL (periodo do cabecalho, `lead=`...). */
export function escreverConsultaNaUrl(search: string, consulta: ConsultaDeLeads): string {
  const params = new URLSearchParams(search);
  for (const chave of Object.values(CHAVES)) {
    params.delete(chave);
  }
  if (consulta.situacao !== null) {
    params.set(CHAVES.situacao, consulta.situacao);
  }
  if (consulta.offerId !== null) {
    params.set(CHAVES.oferta, consulta.offerId);
  }
  if (consulta.canal !== null) {
    params.set(CHAVES.canal, consulta.canal);
  }
  if (consulta.funnel !== null) {
    params.set(CHAVES.funil, consulta.funnel);
  }
  if (consulta.altoValor) {
    params.set(CHAVES.alto, '1');
  }
  if (consulta.q.trim() !== '') {
    params.set(CHAVES.q, consulta.q.trim());
  }
  if (consulta.sort !== CONSULTA_PADRAO.sort || consulta.dir !== CONSULTA_PADRAO.dir) {
    params.set(CHAVES.ordem, consulta.sort);
    params.set(CHAVES.dir, consulta.dir);
  }
  const texto = params.toString();
  return texto === '' ? '' : `?${texto}`;
}

/** Direcao natural ao clicar numa coluna pela primeira vez. */
export function direcaoPadrao(sort: OrdenacaoDeLeads): DirecaoDaOrdem {
  return sort === 'situacao' ? 'asc' : 'desc';
}
